<template>
  <WizardBox title="New Stocktake">
      <template v-slot:tools>
        
      </template>
      <template v-slot:content>
        <FormGroup v-bind:form="stocktakeForm">
          <FormWizard ref="stocktakeWizard" :submitting="submitting" v-bind:tabList="tabList" @finished="handleSubmit">
            <template v-slot:content>
              
              <FormWizardStep index="1" v-bind:active="true">
                <ContentTitle>Stocktake Details</ContentTitle>
                <FormRow label="Stocktake Name">
                  <FormInputText placeholder="Stocktake Name" v-model="stocktake.name" field="name" />
                </FormRow>
                <FormRow label="Account Location">
                  <AccountLocationSelect v-model="stocktake.accountLocationId" field="accountLocationId" :allowNone="false" placeholder="Select Account Location" />
                </FormRow>
                <FormRow label="Assigned Users" helpTip="These users all have access to the stocktake to submit counts">
                  <FormMultiSelect :options="userListLookup" v-model="selectedUsers" placeholder="Users" field="users" />
                </FormRow>
              </FormWizardStep>

              <FormWizardStep index="2">
                <TabbedBox>
                  <template v-slot:tablist>
                    <Tab :first="true" tabid="tab-settings-glass">
                      <i class="far fa-container-storage"></i> Glass Sheets
                    </Tab>
                    <Tab tabid="tab-settings-offcuts">
                      <i class="far fa-layer-group"></i> Glass Offcuts
                    </Tab>
                    <Tab tabid="tab-settings-general">
                      <i class="far fa-box-up"></i> General
                    </Tab>
                  </template>
                  <template v-slot:tabcontent>
                    <TabPage :first="true" tabid="tab-settings-glass">
                      <ActionDropMenu class="float-right">
                        <DropdownMenuItem @click="enableAllGlass" icon="fa-check">Enable All</DropdownMenuItem>
                        <DropdownMenuItem @click="disableAllGlass" icon="fa-times">Disable All</DropdownMenuItem>
                      </ActionDropMenu>
                      <ContentTitle>Glass List</ContentTitle>
                      <CollapsibleTable>
                        <template v-slot:header>
                          <tr>
                            <th>Category</th>
                            <th width="150px">Enabled</th>
                          </tr>
                        </template>
                        <template v-slot:body>
                          <template v-for="(stockCategory, index) in glassStockCategoryList">
                            <CollapsibleRow :key="index" v-show="isCategoryVisible(glassStockCategoryList, stockCategory)">
                              <td class="font-weight-bold" @click="categoryBarClicked($event, stockCategory, true)" :style="categoryBarStyle(stockCategory)">
                                <CollapsibleRowArrow :expanded="stockCategory.expanded" />
                                <span :class="{'stocktake-inactive-item' : !stockCategory.isActive }">{{stockCategory.name}}</span>
                              </td>
                              <td @click="categoryBarClicked($event, stockCategory, true)">
                                <FormSwitch 
                                  :value="getSwitchValue(stockCategory)"
                                  @update="categorySwitchUpdated(glassStockCategoryList, stockCategory, $event)"
                                />
                              </td>
                            </CollapsibleRow>
                            <template v-for="stock in getStockListForCategory(stockCategory)">
                              <CollapsibleRow :key="`${stock.id}-stock`" v-show="stockCategory.expanded && isCategoryVisible(glassStockCategoryList, stockCategory)">
                                <td @click="stockBarClicked($event, stock, true)" :class="{'stocktake-inactive-item' : !stock.isActive}" :style="stockBarStyle(stockCategory)">
                                  <CollapsibleRowArrow :expanded="stock.expanded" />{{stock.name}}
                                </td>
                                <td @click="stockBarClicked($event, stock, true)">
                                  <FormSwitch 
                                    :value="getSwitchValue(stock)"
                                    @update="stockSwitchUpdated(stock, $event)"
                                  />
                                </td>
                              </CollapsibleRow>

                              <template v-for="(glassSize, index) in getGlassSizeListForStock(stock)">
                                <tr :key="`${stock.id}-${index}-glasssize`" class="" v-show="stock.expanded && stockCategory.expanded && isCategoryVisible(glassStockCategoryList, stockCategory)">
                                  <td :class="{'stocktake-inactive-item' : !glassSize.isActive}" :style="stockItemBarStyle(stockCategory)">
                                    {{glassSize.height}} x {{glassSize.width}}
                                  </td>
                                  <td>
                                    <FormSwitch 
                                      :value="getSwitchValue(glassSize)"
                                      @update="glassSizeSwitchUpdated(glassSize, $event)"
                                    />
                                  </td>
                                </tr>
                              </template>
                            </template>
                          </template>
                        </template>
                      </CollapsibleTable>
                    </TabPage>

                    <TabPage tabid="tab-settings-offcuts">
                      <ActionDropMenu class="float-right">
                        <DropdownMenuItem @click="enableAllOffcuts" icon="fa-check">Enable All</DropdownMenuItem>
                        <DropdownMenuItem @click="disableAllOffcuts" icon="fa-times">Disable All</DropdownMenuItem>
                      </ActionDropMenu>
                      <ContentTitle>Offcut List</ContentTitle>
                      <CollapsibleTable>
                        <template v-slot:header>
                          <tr>
                            <th>Category</th>
                            <th width="150px">Enabled</th>
                          </tr>
                        </template>
                        <template v-slot:body>
                          <template v-for="(stockCategory, index) in glassOffcutStockCategoryList">
                            <CollapsibleRow :key="index" v-show="isCategoryVisible(glassOffcutStockCategoryList, stockCategory)">
                              <td class="font-weight-bold" @click="categoryBarClicked($event, stockCategory, true)" :style="categoryBarStyle(stockCategory)">
                                <CollapsibleRowArrow :expanded="stockCategory.expanded" />
                                <span :class="{'stocktake-inactive-item' : !stockCategory.isActive }">{{stockCategory.name}}</span>
                              </td>
                              <td @click="categoryBarClicked($event, stockCategory, true)">
                                <FormSwitch 
                                  :value="getSwitchValue(stockCategory)"
                                  @update="categorySwitchUpdated(glassOffcutCategoryList, stockCategory, $event)"
                                />
                              </td>
                            </CollapsibleRow>
                            <template v-for="stock in getStockListForCategory(stockCategory)">
                              <CollapsibleRow :key="`${stock.id}-stock`" v-show="stockCategory.expanded && isCategoryVisible(glassOffcutStockCategoryList, stockCategory)">
                                <td :class="{'stocktake-inactive-item' : !stock.isActive}" :style="stockBarStyle(stockCategory)">
                                  {{stock.name}}
                                </td>
                                <td>
                                  <FormSwitch 
                                    :value="getOffcutSwitchValue(stock)"
                                    @update="offcutStockSwitchUpdated(stock, $event)"
                                  />
                                </td>
                              </CollapsibleRow>
                            </template>
                          </template>
                        </template>
                      </CollapsibleTable>
                    </TabPage>

                    <TabPage tabid="tab-settings-general">
                      <ActionDropMenu class="float-right">
                        <DropdownMenuItem @click="enableAllGeneral" icon="fa-check">Enable All</DropdownMenuItem>
                        <DropdownMenuItem @click="disableAllGeneral" icon="fa-times">Disable All</DropdownMenuItem>
                      </ActionDropMenu>
                      <ContentTitle>General Stock List</ContentTitle>
                      <CollapsibleTable>
                        <template v-slot:header>
                          <tr>
                            <th>Category</th>
                            <th width="150px">Enabled</th>
                          </tr>
                        </template>
                        <template v-slot:body>
                          <template v-for="(stockCategory, index) in generalStockCategoryList">
                            <CollapsibleRow :key="index">
                              <td class="font-weight-bold" @click="categoryBarClicked($event, stockCategory, true)">
                                <CollapsibleRowArrow :expanded="stockCategory.expanded" />
                                <span :class="{'stocktake-inactive-item' : !stockCategory.isActive }">{{stockCategory.name}}</span>
                              </td>
                              <td @click="categoryBarClicked($event, stockCategory, true)">
                                <FormSwitch 
                                  :value="getSwitchValue(stockCategory)"
                                  @update="categorySwitchUpdated(stockCategoryList, stockCategory, $event)"
                                />
                              </td>
                            </CollapsibleRow>
                            <template v-for="stock in getStockListForCategory(stockCategory)">
                              <tr :key="`${stock.id}-stock`" v-show="stockCategory.expanded">
                                <td @click="stockBarClicked($event, stock, true)">
                                  <span :class="{'stocktake-inactive-item' : !stockCategory.isActive }"><span class="ml-3" />{{stock.name}}</span>
                                </td>
                                <td>
                                  <FormSwitch 
                                    :value="getSwitchValue(stock)"
                                    @update="stockSwitchUpdated(stock, $event)"
                                  />
                                </td>
                              </tr>
                            </template>
                          </template>
                        </template>
                      </CollapsibleTable>
                    </TabPage>

                  </template>
                </TabbedBox>
              </FormWizardStep>

              <FormWizardStep index="3">
                <div class="row review-data">
                  <div class="col-12">
                    <div class="row">
                      <div class="col-6">
                        <div class="review-header">
                          <h2>Stocktake Summary</h2> <a class="review-edit-button" @click="displayDetailsTab"><i class="far fa-pencil"></i></a>
                        </div>
                        <FormRow label="Stocktake Name" :labelColumnSize="5">
                          <FormStaticLabel>{{stocktake.name}}</FormStaticLabel>
                        </FormRow>
                        <FormRow label="Assigned Users" :labelColumnSize="5">
                          <FormStaticLabel>{{selectedUserDisplay}}</FormStaticLabel>
                        </FormRow>
                        <FormRow label="Account Location" :labelColumnSize="5">
                          <FormStaticLabel>{{accountLocationName}}</FormStaticLabel>
                        </FormRow>
                      </div>
                      <div class="col-6">
                        <div class="review-header">
                          <h2>Stock Summary</h2> <a class="review-edit-button" @click="displayStockTab"><i class="far fa-pencil"></i></a>
                        </div>
                        <FormRow label="Stock Sheet Size Count" :labelColumnSize="5">
                          <FormStaticLabel>{{glassStockSizeCount}}</FormStaticLabel>
                        </FormRow>
                        <FormRow label="Offcut Stock Count" :labelColumnSize="5">
                          <FormStaticLabel>{{glassOffcutCount}}</FormStaticLabel>
                        </FormRow>
                        <FormRow label="General Stock Count" :labelColumnSize="5">
                          <FormStaticLabel>{{generalStockCount}}</FormStaticLabel>
                        </FormRow>
                      </div>
                    </div>
                  </div>
                </div>
              </FormWizardStep>
            </template>
          </FormWizard>
        </FormGroup>
      </template>
    </WizardBox>
</template>

<script>
import _ from 'lodash'
import form from '@/mixins/form'
import WizardBox from '@/components/wizardBox'
import FormSwitch from '@/components/form/formSwitch.vue'
import AccountLocationSelect from '@/modules/admin/components/accountLocationSelect'

import { InventoryStockTypes } from '@/store/types'

export default {
  name: 'StockTakeNew',
  mixins: [form],
  components: {
    WizardBox,
    AccountLocationSelect
  },
  data() {
    return {
      stocktake: {
        name: '',
        accountLocationId: null,
      },
      userListLookup: [],
      selectedUsers: [],
      stocktakeForm: {},
      submitting: false,
      loading: false,
      stockCategoryList: [],
      glassOffcutCategoryList: [],
      stockList: [],
      glassSizeList: []
    }
  },
  watch: {
  },
  mounted() {
    this.requestData()
  },
  computed: {
    tabList() {
      var output = [
        { title: 'Details' },
        { title: 'Stock Settings' },
        { title: 'Review' }
      ]
      return output
    },
    glassStockCategoryList() {
      return this.$data.stockCategoryList.filter(stockCategory => stockCategory.stockType == InventoryStockTypes.glass)
    },
    glassOffcutStockCategoryList() {
      return this.$data.glassOffcutCategoryList.filter(stockCategory => stockCategory.stockType == InventoryStockTypes.glass)
    },
    generalStockCategoryList() {
      return this.$data.stockCategoryList.filter(stockCategory => stockCategory.stockType == InventoryStockTypes.general)
    },
    selectedUserDisplay() {
      return this.selectedUsers.map(userId => {
        return this.formatLookupName(userId, this.userListLookup)
      }).join(', ')
    },
    accountLocationName() {
      var accLocation = this.$store.getters['account/getAccLocationById'](this.stocktake.accountLocationId)
      if(accLocation)
        return accLocation.name

      return ''
    },
    glassStockSizeCount() {
      var sizeCount = 0

      this.$data.stockList.forEach(stock => {
        if(stock.type == InventoryStockTypes.glass) {
          sizeCount += this.getGlassSizeListForStock(stock).filter(size => size.display != FormSwitch.ValueSettings.unchecked).length
        }        
      })
      return sizeCount
    },
    glassOffcutCount() {
      var offcutCount = 0
      offcutCount = this.$data.stockList.filter(stock => {
        if(stock.type == InventoryStockTypes.glass && stock.offcutDisplay != FormSwitch.ValueSettings.unchecked) {
          return true
        }
        return false
      }).length

      return offcutCount
    },
    generalStockCount() {
      
      var generalCount = this.$data.stockList.filter(stock => {
        if(stock.type == InventoryStockTypes.general && stock.display != FormSwitch.ValueSettings.unchecked) {
          return true
        }
        return false
      }).length
      return generalCount
    }
  },
  methods: {
    handleSubmit() {
    
      this.submitting = true

      // Send all the category / stock / glasssize enabled/disabled data
      let stockSettings = this.$data.stockList.map(stock => {

        var stockSetting = {
          stockId: stock.id,
          isEnabled: stock.display != FormSwitch.ValueSettings.unchecked,
          isOffcutsEnabled: false
        }

        if(stock.type == InventoryStockTypes.glass) {
          stockSetting.isOffcutsEnabled = stock.offcutDisplay != FormSwitch.ValueSettings.unchecked

          stockSetting.glassSizeConfig = this.getGlassSizeListForStock(stock)
          .map(size => {
            return {
              glassSizeId: size.id,
              isEnabled: size.display != FormSwitch.ValueSettings.unchecked
            }
          })

          // Stock is enabled if any size is set or offcuts are enabled
          stockSetting.isEnabled = stockSetting.glassSizeConfig.filter(size => size.isEnabled).length > 0 || stockSetting.isOffcutsEnabled
        }
        return stockSetting
      })

      var stocktakeData = {
        name: this.stocktake.name,
        accountLocationId: this.stocktake.accountLocationId,
        users: this.selectedUsers,
        stockConfiguration: stockSettings
      }

      this.$apiService.inventoryService.createStocktake(stocktakeData)
      .then(response => {
        this.successToast('Stocktake Created')
        this.$router.push(`/inventory/stocktakes/${response.data.id}`)
      })
      .catch(err => {
        this.$data.stocktakeForm = this.processFormResponse(err)
        this.$refs.stocktakeWizard.displayErrors()
      })
      .finally(() => {
        this.submitting = false
      })
    },

    requestData() {
      this.loading = true

      Promise.all([
        this.$apiService.inventoryService.getStockCategoryList(),
        this.$apiService.inventoryService.getStockList(),
        this.$apiService.inventoryService.getGlassSizeList(),
        this.$apiService.userService.getUserList()
      ])
      .then(results => {
        // Category list
        // Default to all enabled
        this.$data.stockCategoryList = results[0].data.filter(category => {
          return category.stockType == InventoryStockTypes.glass || category.stockType == InventoryStockTypes.general
        }).map(category => {
          return {
            id: category.id,
            parentCategoryId: category.parentCategoryId,
            depth: this.calculateCategoryDepth(results[0].data, category.id),
            name: category.name,
            stockType: category.stockType,
            expanded: false,
            isEnabled: true,
            isActive: category.isActive,
            display: FormSwitch.ValueSettings.checked
          }
        })
        this.glassOffcutCategoryList = _.cloneDeep(this.$data.stockCategoryList)
        this.glassOffcutCategoryList.forEach(category => {
          category.isOffcut = true

          // Default all offcut categoryies to unselected
          category.isEnabled = false
          category.display = FormSwitch.ValueSettings.unchecked
        })

        // Stock List
        // Ignore service stock or general stock that does not have trackable quantities
        this.$data.stockList = results[1].data.filter(stock => {
          if(stock.type == InventoryStockTypes.glass)
            return true
          if(stock.type == InventoryStockTypes.general && stock.isTrackingQuantity)
            return true
          return false
        }).map(stock => {
          stock = _.merge(stock, {
            expanded: false,
            isEnabled: true,
            display: FormSwitch.ValueSettings.checked,

            isOffcutEnabled: false,
            offcutDisplay: FormSwitch.ValueSettings.unchecked
          })
          return stock
        })

        // Auto-expand categories with no stock
        this.$data.stockCategoryList.forEach(category => {
          if(this.getStockListForCategory(category).length === 0) {
            category.expanded = true
          }
        })
        this.$data.glassOffcutCategoryList.forEach(category => {
          if(this.getStockListForCategory(category).length === 0) {
            category.expanded = true
          }
        })


        this.$data.glassSizeList = results[2].data.map(glassSize => {
          return _.merge(glassSize, { isEnabled: true, display: FormSwitch.ValueSettings.checked })
        })

        // User list
        this.userListLookup = results[3].data
        .filter(user => user.isActive)
        .map(user => {
          return {
            value: user.id,
            name: user.name,
            colour: user.highlightColour
          }
        })

        this.updateCategoryStates(this.stockCategoryList)
        this.updateCategoryStates(this.glassOffcutCategoryList)
      })
      .catch(err => {
        console.error(err)
      })
      .finally(() => {
        this.loading = false
      })
    },

    isCategoryVisible(categoryList, category) {
      // Check if all parent categories are expanded
      // If root category, always show
      if(!category.parentCategoryId)
        return true

      let parentCategory = categoryList.find(cat => cat.id == category.parentCategoryId)
      if(!parentCategory)
        return true

      return parentCategory.expanded && this.isCategoryVisible(categoryList, parentCategory)
    },

    getStockListForCategory(category) {
      return this.$data.stockList.filter(stock => stock.stockCategoryId == category.id)
    },
    getGlassSizeListForStock(stock) {
      return this.$data.glassSizeList.filter(glassSize => glassSize.stockId == stock.id && glassSize.isActive)
    },
    categoryBarClicked(evt, category, nameColumn) {
      // Ignore clicks on switches
      if($(evt.target).is(':input') || $(evt.target).is('label'))
        return true

      category.expanded = !category.expanded
      return true
    },
    categorySwitchUpdated(categoryList, category, value, nested) {
      // Update child categories too
      var childCategories = categoryList.filter(cat => cat.parentCategoryId == category.id)
      childCategories.forEach(cat => {
        cat.display = value
        cat.isEnabled = (value == FormSwitch.ValueSettings.checked)
        this.categorySwitchUpdated(categoryList, cat, value, true)
      })

      // Update all children category/stock items
      this.$data.stockList.forEach(stock => {
        if(stock.stockCategoryId != category.id)
          return

        if(category.isOffcut) {
          stock.offcutDisplay = value
          stock.isOffcutEnabled = (value == FormSwitch.ValueSettings.checked)
          return
        }

        stock.display = value
        stock.isEnabled = (value == FormSwitch.ValueSettings.checked)

        // Update all glass sizes
        this.getGlassSizeListForStock(stock).forEach(size => {
          size.display = value
          size.isEnabled = (value == FormSwitch.ValueSettings.checked)
        })

      })
      if(!nested) {
        this.updateCategoryStates(categoryList)
      }
    },
    updateCategoryStates(categoryList) {
      // Update the category states if a stock was modified
      // If a category contains child stock/categories in a mixed state, change the state to interminate
      categoryList.forEach(item => {
        let showCount = 0
        let hideCount = 0

        if(item.isOffcut) {
          showCount = this.getOffcutDisplayStateCount(item.id, FormSwitch.ValueSettings.checked)
          hideCount = this.getOffcutDisplayStateCount(item.id, FormSwitch.ValueSettings.unchecked)
        } else {
          showCount = this.getDisplayStateCount(item.id, FormSwitch.ValueSettings.checked)
          hideCount = this.getDisplayStateCount(item.id, FormSwitch.ValueSettings.unchecked)
        }

        // Don't change anything if no children
        if(showCount == 0 && hideCount == 0)
          return

        var display = FormSwitch.ValueSettings.unchecked
        if(showCount > 0)
          display = FormSwitch.ValueSettings.checked
        if(showCount > 0 && hideCount > 0)
          display = FormSwitch.ValueSettings.indeterminate

        item.display = display
      })

      // Update the stock states if a glass size was modified
      this.$data.stockList.forEach(stock => {
        var showCount = this.getGlassSizeListForStock(stock).filter(size => size.display == FormSwitch.ValueSettings.checked).length
        var hideCount = this.getGlassSizeListForStock(stock).filter(size => size.display == FormSwitch.ValueSettings.unchecked).length

        if(showCount == 0 && hideCount == 0)
          return

        var display = FormSwitch.ValueSettings.unchecked
        if(showCount > 0)
          display = FormSwitch.ValueSettings.checked
        if(showCount > 0 && hideCount > 0)
          display = FormSwitch.ValueSettings.indeterminate

        stock.display = display
      })
    },
    getDisplayStateCount(parentCategoryId, displayState) {
      var displayCount = 0

      this.$data.stockList.forEach(stock => {
        if(stock.stockCategoryId != parentCategoryId)
          return

        if(stock.display == displayState)
          displayCount++

        // Glass Sizes
        displayCount += this.getGlassSizeListForStock(stock).filter(size => size.display == displayState).length
      })

      return displayCount
    },
    getOffcutDisplayStateCount(parentCategoryId, displayState) {
      var displayCount = 0

      this.$data.stockList.forEach(stock => {
        if(stock.stockCategoryId != parentCategoryId)
          return

        if(stock.offcutDisplay == displayState)
          displayCount++
      })

      return displayCount
    },
    stockBarClicked(evt, stock, nameColumn) {
      // Ignore clicks on switches
      if($(evt.target).is(':input') || $(evt.target).is('label'))
        return true

      stock.expanded = !stock.expanded
      return true
    },
    getSwitchValue(item) {
      if(!item.display)
        return FormSwitch.ValueSettings.checked

      return item.display
    },
    getOffcutSwitchValue(item) {
      if(!item.offcutDisplay)
        return FormSwitch.ValueSettings.unchecked

      return item.offcutDisplay
    },
    stockSwitchUpdated(stock, value) {
      stock.display = value
      stock.isEnabled = (value == FormSwitch.ValueSettings.checked)

      // Update all glass sizes
      this.getGlassSizeListForStock(stock).forEach(size => {
        size.display = value
        size.isEnabled = (value == FormSwitch.ValueSettings.checked)
      })

      this.updateCategoryStates(this.stockCategoryList)
    },
    offcutStockSwitchUpdated(stock, value) {
      stock.offcutDisplay = value
      stock.isOffcutEnabled = (value == FormSwitch.ValueSettings.checked)

      this.updateCategoryStates(this.glassOffcutCategoryList)
    },
    glassSizeSwitchUpdated(glassSize, value) {
      glassSize.display = value
      glassSize.isEnabled = (value == FormSwitch.ValueSettings.checked)

      this.updateCategoryStates(this.stockCategoryList)
    },

    enableAllGlass() {
      this.$data.stockCategoryList.forEach(category => {
        if(category.stockType != InventoryStockTypes.glass)
          return
        category.display = FormSwitch.ValueSettings.checked
        category.isEnabled = (category.display == FormSwitch.ValueSettings.checked)
      })

      this.$data.stockList.forEach(stock => {
        if(stock.type != InventoryStockTypes.glass)
          return

        stock.display = FormSwitch.ValueSettings.checked
        stock.isEnabled = (stock.display == FormSwitch.ValueSettings.checked)

        this.getGlassSizeListForStock(stock).forEach(size => {
          size.display = stock.display
          size.isEnabled = (size.display == FormSwitch.ValueSettings.checked)
        })
      })
      this.updateCategoryStates(this.stockCategoryList)
    },
    disableAllGlass() {
      this.$data.stockCategoryList.forEach(category => {
        if(category.stockType != InventoryStockTypes.glass)
          return

        category.display = FormSwitch.ValueSettings.unchecked
        category.isEnabled = (category.display == FormSwitch.ValueSettings.checked)
      })

      this.$data.stockList.forEach(stock => {
        if(stock.type != InventoryStockTypes.glass)
          return

        stock.display = FormSwitch.ValueSettings.unchecked
        stock.isEnabled = (stock.display == FormSwitch.ValueSettings.checked)

        this.getGlassSizeListForStock(stock).forEach(size => {
          size.display = stock.display
          size.isEnabled = (size.display == FormSwitch.ValueSettings.checked)
        })
      })
      this.updateCategoryStates(this.stockCategoryList)
    },
    enableAllOffcuts() {
      this.$data.glassOffcutCategoryList.forEach(category => {
        if(category.stockType != InventoryStockTypes.glass)
          return
        category.display = FormSwitch.ValueSettings.checked
        category.isEnabled = (category.display == FormSwitch.ValueSettings.checked)
      })

      this.$data.stockList.forEach(stock => {
        if(stock.type != InventoryStockTypes.glass)
          return

        stock.offcutDisplay = FormSwitch.ValueSettings.checked
        stock.isOffcutEnabled = (stock.offcutDisplay == FormSwitch.ValueSettings.checked)
      })
      this.updateCategoryStates(this.glassOffcutCategoryList)
    },
    disableAllOffcuts() {
      this.$data.glassOffcutCategoryList.forEach(category => {
        if(category.stockType != InventoryStockTypes.glass)
          return
        category.display = FormSwitch.ValueSettings.unchecked
        category.isEnabled = (category.display == FormSwitch.ValueSettings.checked)
      })

      this.$data.stockList.forEach(stock => {
        if(stock.type != InventoryStockTypes.glass)
          return

        stock.offcutDisplay = FormSwitch.ValueSettings.unchecked
        stock.isOffcutEnabled = (stock.offcutDisplay == FormSwitch.ValueSettings.checked)
      })
      this.updateCategoryStates(this.glassOffcutCategoryList)
    },
    
    enableAllGeneral() {
      this.$data.stockCategoryList.forEach(category => {
        if(category.stockType != InventoryStockTypes.general)
          return
        category.display = FormSwitch.ValueSettings.checked
        category.isEnabled = (category.display == FormSwitch.ValueSettings.checked)
      })

      this.$data.stockList.forEach(stock => {
        if(stock.type != InventoryStockTypes.general)
          return
        stock.display = FormSwitch.ValueSettings.checked
        stock.isEnabled = (stock.display == FormSwitch.ValueSettings.checked)
      })
      this.updateCategoryStates(this.stockCategoryList)
    },
    disableAllGeneral() {
      this.$data.stockCategoryList.forEach(category => {
        if(category.stockType != InventoryStockTypes.general)
          return
        category.display = FormSwitch.ValueSettings.unchecked
        category.isEnabled = (category.display == FormSwitch.ValueSettings.checked)
      })

      this.$data.stockList.forEach(stock => {
        if(stock.type != InventoryStockTypes.general)
          return
        stock.display = FormSwitch.ValueSettings.unchecked
        stock.isEnabled = (stock.display == FormSwitch.ValueSettings.checked)
      })
      this.updateCategoryStates(this.stockCategoryList)
    },
    categoryBarStyle(stockCategory) {
      let padding = (stockCategory.depth * 15) + 'px'
      return `padding-left: ${padding};`
    },
    stockBarStyle(stockCategory) {
      let padding = ((stockCategory.depth + 1) * 15) + 'px'
      return `padding-left: ${padding};`
    },
    stockItemBarStyle(stockCategory) {
      let padding = ((stockCategory.depth + 2) * 15) + 'px'
      return `padding-left: ${padding};`
    },
    calculateCategoryDepth(categoryList, categoryId, depth = 0) {
      let category = categoryList.find(cat => cat.id == categoryId)
      if(!category)
        return depth

      if(category.parentCategoryId) {
        return this.calculateCategoryDepth(categoryList, category.parentCategoryId, depth + 1)
      }
      return depth
    },
    displayDetailsTab() {
      this.$refs.stocktakeWizard.selectTab(0)
    },
    displayStockTab() {
      this.$refs.stocktakeWizard.selectTab(1)
    }
  }
};
</script>
